<template>
  <div class="flex h-full flex-col">
    <div class="flex-1 overflow-y-auto px-5 py-4">
      <div v-for="(group, date) in groupedFixtures" :key="date" class="mb-5 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <!-- Date Header -->
        <div class="sticky top-0 z-10 flex items-center justify-between bg-slate-900 px-4 py-2.5 text-white">
          <div class="flex items-center gap-2">
            <span class="flex h-6 w-6 items-center justify-center rounded-md bg-white/10">
              <i class="fas fa-calendar text-[10px]"></i>
            </span>
            <span class="text-sm font-medium tracking-tight">{{ formatDate(date) }}</span>
          </div>
          <span class="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-slate-200">{{ group.length }} {{ group.length === 1 ? 'game' : 'games' }}</span>
        </div>
        
        <!-- Table Header -->
        <div class="border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
          <div class="grid grid-cols-12 gap-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <div class="col-span-1">{{ type === 'fixture' ? 'Fixture' : 'Outright' }} ID</div>
            <div class="col-span-1">Time</div>
            <div class="col-span-4">{{ type === 'fixture' ? 'Teams' : 'Name' }}</div>
            <div class="col-span-2">League</div>
            <div class="col-span-2">Status</div>
            <div class="col-span-2 text-right">Actions</div>
          </div>
        </div>
        
        <!-- Fixtures for this date -->
        <div class="bg-white">
          <FixtureSearchResultItem
            v-for="(fixture, index) in group" 
            :key="fixture.id"
            :fixture="fixture"
            :type="type"
            :isSelected="getGlobalIndex(date, index) === selectedIndex"
            :isLast="index === group.length - 1"
            @select="$emit('select', fixture)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue'
import FixtureSearchResultItem from './FixtureSearchResultItem.vue'

const props = defineProps({
  fixtures: {
    type: Array,
    required: true
  },
  selectedIndex: {
    type: Number,
    default: 0
  },
  type: {
    type: String,
    default: 'fixture', // 'fixture' or 'outright'
    validator: (value) => ['fixture', 'outright'].includes(value)
  }
})

const emit = defineEmits(['select', 'view'])

const orderBy = ref('gamedate')

const sortedFixtures = computed(() => {
  const arr = [...props.fixtures]
  arr.sort((a, b) => {
    const aVal = orderBy.value === 'gamedate' ? a.gamedate : a.created_at
    const bVal = orderBy.value === 'gamedate' ? b.gamedate : b.created_at
    // Newest first
    return (bVal || '').localeCompare(aVal || '')
  })
  return arr
})

const groupedFixtures = computed(() => {
  const groups = {}
  
  sortedFixtures.value.forEach(fixture => {
    const date = fixture.gamedate ? fixture.gamedate.split(' ')[0] : 'No Date'
    
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(fixture)
  })
  
  return groups
})

// Method to calculate global index across all groups
const getGlobalIndex = (currentDate, localIndex) => {
  let globalIndex = 0
  
  // Get all dates in order
  const orderedDates = Object.keys(groupedFixtures.value).sort((a, b) => {
    if (a === 'No Date') return 1
    if (b === 'No Date') return -1
    return b.localeCompare(a) // Newest first to match sorting
  })
  
  // Calculate global index by adding up previous groups
  for (const date of orderedDates) {
    if (date === currentDate) {
      return globalIndex + localIndex
    }
    globalIndex += groupedFixtures.value[date].length
  }
  
  return globalIndex
}

const formatDate = (dateString) => {
  if (dateString === 'No Date') return 'No Date'
  
  // Handle European format directly since that's what we're getting from gamedate.split(' ')[0]
  if (dateString.includes('.')) {
    const [day, month, year] = dateString.split('.')
    
    // Validate the parts exist
    if (!day || !month || !year) return dateString
    
    // Format to dd.mm.yyyy (full year for header)
    return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`
  }
  
  // Fallback for other formats
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).toString().padStart(2, '0')
    const year = String(date.getFullYear())
    
    return `${day}.${month}.${year}`
  } catch (error) {
    return dateString
  }
}
</script>
